import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReadOnlyCanvas from '../components/shared/ReadOnlyCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public, view-only renderer for a shared user design.
 * URL: /design/:shareToken
 *
 * Calls GET /api/public/designs/{shareToken}. No auth header sent —
 * the token IS the credential. Renders the actual design content via
 * ReadOnlyCanvas (the same renderer the editor uses internally), so
 * what the recipient sees matches the editor pixel-for-pixel.
 *
 * No editing UI mounted. No collaboration features yet (planned).
 */
export default function DesignViewerPage() {
  const { shareToken } = useParams();
  const [design, setDesign] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/public/designs/${shareToken}`)
      .then((r) => { if (!cancelled) setDesign(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'not_found' : 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) {
    return <div className="p-12 text-center text-gray-500">Loading…</div>;
  }
  if (error === 'not_found') {
    return (
      <div className="p-12 text-center">
        <p className="text-lg text-gray-700">Design no longer available.</p>
        <p className="text-sm text-gray-500 mt-2">
          The owner may have revoked this share link.
        </p>
      </div>
    );
  }
  if (!design) {
    return <div className="p-12 text-center text-gray-700">Something went wrong.</div>;
  }

  const canvasSize = design.canvas_size || { width: 1080, height: 1080 };
  const pages = Array.isArray(design.pages) ? design.pages : [];

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8">
      <header className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
          {design.name || 'Untitled Design'}
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          View-only shared design · {pages.length} {pages.length === 1 ? 'page' : 'pages'} · {canvasSize.width}×{canvasSize.height}
        </p>
      </header>

      <ReadOnlyCanvas
        pages={pages}
        canvasSize={canvasSize}
        currentPageId={design.current_page_id}
      />

      <p className="text-xs text-gray-400 mt-6 sm:mt-8 text-center">
        Shared with iVisora · view-only · collaboration coming soon
      </p>
    </div>
  );
}
